/**
 * Document Import Notification Banner
 *
 * Shows a dismissible banner when document processing completes.
 * Tappable to navigate to DocumentReviewScreen.
 * THEME-AWARE - Uses theme's primary color for background
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDocumentImportContext } from '../../contexts/DocumentImportContext';
import { useTheme } from '../../contexts/ThemeContext';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';

export function DocumentImportBanner() {
  const { theme } = useTheme();
  const { activeImport, clearImport } = useDocumentImportContext();
  const navigation = useNavigation<StackNavigationProp<PortfolioNavigatorParamList>>();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Show when complete or failed
  if (!activeImport || (activeImport.status !== 'complete' && activeImport.status !== 'failed')) {
    return null;
  }

  const isFailed = activeImport.status === 'failed';

  const handlePress = () => {
    if (!isFailed) {
      navigation.navigate(
        'DocumentReview' as never,
        {
          importId: activeImport.importId,
        } as never
      );
      clearImport(); // Clear after navigation
    }
  };

  const handleDismiss = () => {
    clearImport();
  };

  return (
    <TouchableOpacity
      style={[
        styles.container,
        { backgroundColor: isFailed ? theme.colors.error : theme.colors.primary },
      ]}
      onPress={handlePress}
      activeOpacity={isFailed ? 1 : 0.9}
    >
      <View style={styles.content}>
        <Text style={[styles.icon, { color: theme.colors.textInverse }]}>
          {isFailed ? '⚠' : '✓'}
        </Text>
        <View style={styles.textContainer}>
          <Text style={[styles.title, { color: theme.colors.textInverse }]}>
            {isFailed ? 'Import Failed' : 'Import Complete!'}
          </Text>
          <Text style={[styles.message, { color: theme.colors.textInverse }]}>
            {isFailed
              ? activeImport.errorMessage ||
                'An error occurred during processing. Please try again.'
              : 'Tap to review extracted data'}
          </Text>
        </View>
        <TouchableOpacity onPress={handleDismiss} style={styles.dismissButton}>
          <Text style={[styles.dismissText, { color: theme.colors.textInverse }]}>✕</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderBottomColor: theme.colors.border,
      zIndex: 20,
    },
    content: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    icon: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginRight: theme.spacing.sm,
    },
    textContainer: {
      flex: 1,
    },
    title: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    message: {
      fontSize: theme.typography.fontSizeSm,
      opacity: 0.9,
    },
    dismissButton: {
      padding: theme.spacing.xs,
    },
    dismissText: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
    },
  });
}
